import dayjs, { type ConfigType, type Dayjs, type ManipulateType, type OpUnitType } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const LOCAL_DATETIME_FORMAT = "YYYY-MM-DDTHH:mm";

export type DateInput = ConfigType | null | undefined;

export type WMUDatesAPI = {
  dayjs: typeof dayjs;
  now: () => Dayjs;
  parseISO: (value: string | null | undefined) => Dayjs | null;
  parseLocalDateTimeInput: (value: string | null | undefined) => Dayjs | null;
  formatLocalDateTimeInput: (value: DateInput) => string;
  format: (value: DateInput, token: string) => string;
  toISOString: (value: DateInput) => string;
  toEpochMs: (value: DateInput) => number;
  diffSeconds: (start: DateInput, end: DateInput) => number | null;
  isBefore: (left: DateInput, right: DateInput) => boolean;
  isAfter: (left: DateInput, right: DateInput) => boolean;
  add: (value: DateInput, amount: number, unit: string) => Dayjs | null;
  startOf: (value: DateInput, unit: string) => Dayjs | null;
  endOf: (value: DateInput, unit: string) => Dayjs | null;
};

function toDayjs(value: DateInput): Dayjs | null {
  if (value == null || value === "") return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
}

function parseISO(value: string | null | undefined): Dayjs | null {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed : null;
}

function parseLocalDateTimeInput(value: string | null | undefined): Dayjs | null {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const parsed = dayjs(raw, LOCAL_DATETIME_FORMAT, true);
  return parsed.isValid() ? parsed : null;
}

function formatLocalDateTimeInput(value: DateInput): string {
  const parsed = toDayjs(value);
  return parsed ? parsed.format(LOCAL_DATETIME_FORMAT) : "";
}

function format(value: DateInput, token: string): string {
  const parsed = toDayjs(value);
  return parsed ? parsed.format(token) : "";
}

function toISOString(value: DateInput): string {
  const parsed = toDayjs(value);
  return parsed ? parsed.toISOString() : "";
}

function toEpochMs(value: DateInput): number {
  const parsed = toDayjs(value);
  return parsed ? parsed.valueOf() : Number.NaN;
}

function diffSeconds(start: DateInput, end: DateInput): number | null {
  const left = toDayjs(start);
  const right = toDayjs(end);
  if (!left || !right) return null;
  return right.diff(left, "second");
}

function isBefore(left: DateInput, right: DateInput): boolean {
  const leftDate = toDayjs(left);
  const rightDate = toDayjs(right);
  return Boolean(leftDate && rightDate && leftDate.isBefore(rightDate));
}

function isAfter(left: DateInput, right: DateInput): boolean {
  const leftDate = toDayjs(left);
  const rightDate = toDayjs(right);
  return Boolean(leftDate && rightDate && leftDate.isAfter(rightDate));
}

function add(value: DateInput, amount: number, unit: string): Dayjs | null {
  const parsed = toDayjs(value);
  return parsed ? parsed.add(amount, unit as ManipulateType) : null;
}

function startOf(value: DateInput, unit: string): Dayjs | null {
  const parsed = toDayjs(value);
  return parsed ? parsed.startOf(unit as OpUnitType) : null;
}

function endOf(value: DateInput, unit: string): Dayjs | null {
  const parsed = toDayjs(value);
  return parsed ? parsed.endOf(unit as OpUnitType) : null;
}

const api: WMUDatesAPI = {
  dayjs,
  now: () => dayjs(),
  parseISO,
  parseLocalDateTimeInput,
  formatLocalDateTimeInput,
  format,
  toISOString,
  toEpochMs,
  diffSeconds,
  isBefore,
  isAfter,
  add,
  startOf,
  endOf,
};

window.WMUDates = api;
